import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Pause, Play, Undo2 } from 'lucide-react';
import Navigation from '../components/Navigation/Navigation';
import apiClient from '../utils/apiClient';
import { API_BASE_URL } from '../config/api';
import {
  ALL, BY, CX, CY, Counts, FAM, INSTR, INSTRUMENTS, Instrument, MV, PRESETS, S, at, instrumentForName,
  layout, partsFor, rests, total, zero,
} from '../components/brahms/work';
import { Sampler } from '../components/brahms/sampler';
import './BrahmsRequiemPage.css';

// The Brahms Requiem planning page (app/brahms-requiem/README.md): the orchestra map for
// St. Luke's (moved here from listen.datu.dev/w/brahms-requiem on 2026-11-02), a score player
// that sounds Brahms's lines through the map, and orchestration layers that move a stretch of
// a line to another instrument. Brahms's notes are never changed; a layer is kept apart.

interface Reassign { line: string; bars: [number, number]; to: string; octave: number }
interface Layer { id: string; name: string; counts: Counts; reassign: Reassign[]; saved?: string }
interface Kept { name: string; counts: Counts; kept: string }
interface ScoreLine { id: string; name: string; staff: number; notes: [number, number, number, number][] }
interface Score { movement: number; tempo: number; source?: string; bars: { n: number; start: number; length: number }[]; lines: ScoreLine[]; end: number }
interface Ev { t: number; d: number; m: number; v: number; line: string; instr: Instrument }
interface Snapshot { counts: Counts; reassign: Reassign[]; layerId: string | null }

const OCTAVES: [number, string][] = [[-2, 'two octaves down'], [-1, 'an octave down'], [0, 'as written'], [1, 'an octave up'], [2, 'two octaves up']];
const octaveWords = (o: number) => (OCTAVES.find(([n]) => n === o) || [0, `${o} octaves`])[1];
const errText = (e: unknown) => {
  const r = (e as { response?: { data?: { error?: unknown } } }).response;
  const m = r?.data?.error;
  return typeof m === 'string' ? m : (m as { message?: string })?.message || (e as Error).message || 'Something went wrong.';
};
const slug = (name: string) =>
  (name.toLowerCase().normalize('NFD').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 50) || 'layer') +
  '-' + Math.random().toString(36).slice(2, 6);
// A PDF opened in a new tab cannot send the sign-in header, so the token goes in the address.
const proofUrl = (file: string) => `${API_BASE_URL}/brahms-requiem/proofs/${encodeURIComponent(file)}?token=${encodeURIComponent(localStorage.getItem('token') || '')}`;
const sameCounts = (a: Counts, b: Counts) => S.every(s => (a[s.id] || 0) === (b[s.id] || 0));

// The instrument a line or a reassignment target sounds on.
function resolveTarget(to: string, score: Score | null): Instrument | null {
  const line = score?.lines.find(l => l.id === to);
  if (line) return instrumentForName(line.name);
  return INSTR[to] || null;
}
const covered = (i: Instrument, c: Counts) => !i.section || (c[i.section] || 0) > 0;

// Brahms's lines played through a layer: each note on the instrument that has it at that bar.
function buildEvents(score: Score, reassign: Reassign[]): Ev[] {
  const starts = score.bars.map(b => b.start);
  const barAt = (t: number) => {
    let lo = 0, hi = starts.length - 1;
    while (lo < hi) { const mid = (lo + hi + 1) >> 1; if (starts[mid] <= t + 1e-6) lo = mid; else hi = mid - 1; }
    return score.bars[lo].n;
  };
  const out: Ev[] = [];
  for (const line of score.lines) {
    const own = instrumentForName(line.name) || INSTR.Piano;
    const rules = reassign.filter(r => r.line === line.id);
    for (const [t, d, m, v] of line.notes) {
      let instr = own, pitch = m;
      if (rules.length) {
        const n = barAt(t);
        for (const r of rules) {
          if (n >= r.bars[0] && n <= r.bars[1]) {
            const to = resolveTarget(r.to, score);
            if (to) { instr = to; pitch = m + 12 * r.octave; }
          }
        }
      }
      out.push({ t, d, m: pitch, v, line: line.id, instr });
    }
  }
  return out.sort((a, b) => a.t - b.t);
}

const BrahmsRequiemPage: React.FC = () => {
  // ------------------------------------------------------------ the plan
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [counts, setCounts] = useState<Counts>(PRESETS[0].counts);
  const [kept, setKept] = useState<Kept[]>([]);
  const [reassign, setReassign] = useState<Reassign[]>([]);
  const [layerId, setLayerId] = useState<string | null>(null);
  const [layers, setLayers] = useState<Layer[]>([]);
  const [mv, setMv] = useState(0);
  const [undo, setUndo] = useState<Snapshot[]>([]);
  const [status, setStatus] = useState('');
  const [movements, setMovements] = useState<number[]>([]);
  const [proofs, setProofs] = useState<string[]>([]);
  const firstSave = useRef(true);

  // ------------------------------------------------------------ the player
  const [scoreMv, setScoreMv] = useState(1);
  const [score, setScore] = useState<Score | null>(null);
  const [scoreError, setScoreError] = useState('');
  const [playing, setPlaying] = useState(false);
  const [barIdx, setBarIdx] = useState(0);
  const [bpm, setBpm] = useState(60);
  const [muted, setMuted] = useState<Set<string>>(new Set());
  const [solo, setSolo] = useState<Set<string>>(new Set());
  const [sound, setSound] = useState('');

  // ------------------------------------------------------------ modals
  const [keepOpen, setKeepOpen] = useState(false);
  const [keepName, setKeepName] = useState('');
  const [keepErr, setKeepErr] = useState('');
  const [delKept, setDelKept] = useState(-1);
  const [saveOpen, setSaveOpen] = useState(false);
  const [layerName, setLayerName] = useState('');
  const [layerErr, setLayerErr] = useState('');
  const [delLayer, setDelLayer] = useState<Layer | null>(null);

  // ------------------------------------------------------------ the reassignment form
  const [fLine, setFLine] = useState('');
  const [fFrom, setFFrom] = useState(1);
  const [fTo, setFTo] = useState(1);
  const [fTarget, setFTarget] = useState('');
  const [fOct, setFOct] = useState(0);
  const [fErr, setFErr] = useState('');

  useEffect(() => {
    apiClient.get('/brahms-requiem').then(r => {
      const d = r.data.data;
      const plan = d.plan || {};
      if (plan.orchestra?.counts) setCounts(Object.assign(zero(), plan.orchestra.counts));
      setKept(plan.orchestra?.kept || []);
      setReassign(plan.draft?.reassign || []);
      setLayerId(plan.draft?.layer || null);
      setLayers(d.layers || []);
      setMovements(d.movements || []);
      setProofs(d.proofs || []);
      if ((d.movements || []).length && !(d.movements as number[]).includes(1)) setScoreMv(d.movements[0]);
      setStatus(plan.saved ? 'Saved' : '');
      setLoaded(true);
    }).catch(e => { setLoadError(errText(e)); setLoaded(true); });
  }, []);

  useEffect(() => {
    if (!loaded || loadError) return;
    if (!movements.includes(scoreMv)) { setScore(null); return; }
    setScoreError('');
    apiClient.get(`/brahms-requiem/events/${scoreMv}`).then(r => {
      const sc = r.data as Score;
      setScore(sc); setBpm(sc.tempo || 60); setBarIdx(0);
      setFLine(sc.lines[0]?.id || ''); setFFrom(sc.bars[0]?.n || 1); setFTo(sc.bars[sc.bars.length - 1]?.n || 1);
    }).catch(e => setScoreError(errText(e)));
  }, [loaded, loadError, movements, scoreMv]);

  // Autosave the map and the draft, half a second after the last change.
  useEffect(() => {
    if (!loaded || loadError) return;
    if (firstSave.current) { firstSave.current = false; return; }
    setStatus('Saving…');
    const t = setTimeout(() => {
      apiClient.put('/brahms-requiem/plan', { orchestra: { counts, kept }, draft: { layer: layerId, reassign } })
        .then(() => setStatus('Saved'))
        .catch(e => setStatus(`Not saved: ${errText(e)}`));
    }, 500);
    return () => clearTimeout(t);
  }, [counts, kept, reassign, layerId, loaded, loadError]);

  const snapshot = useCallback((): Snapshot => ({ counts, reassign, layerId }), [counts, reassign, layerId]);
  const change = useCallback((next: Partial<Snapshot>) => {
    setUndo(u => [...u.slice(-199), snapshot()]);
    if (next.counts) setCounts(next.counts);
    if (next.reassign) setReassign(next.reassign);
    if (next.layerId !== undefined) setLayerId(next.layerId);
  }, [snapshot]);
  const bump = (id: string, by: number) => change({ counts: { ...counts, [id]: Math.max(0, (counts[id] || 0) + by) } });
  const doUndo = useCallback(() => {
    setUndo(u => {
      if (!u.length) return u;
      const last = u[u.length - 1];
      setCounts(last.counts); setReassign(last.reassign); setLayerId(last.layerId);
      return u.slice(0, -1);
    });
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (document.activeElement?.tagName || '').toUpperCase();
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !['INPUT', 'SELECT', 'TEXTAREA'].includes(tag)) { e.preventDefault(); doUndo(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [doUndo]);

  // ------------------------------------------------------------ the engine
  const events = useMemo(() => (score ? buildEvents(score, reassign) : []), [score, reassign]);
  const live = useRef({ counts, muted, solo });
  live.current = { counts, muted, solo };
  const eng = useRef<{ sampler: Sampler | null; list: Ev[]; idx: number; t0: number; startBeat: number; spb: number; timer: number; raf: number; on: boolean }>(
    { sampler: null, list: [], idx: 0, t0: 0, startBeat: 0, spb: 1, timer: 0, raf: 0, on: false });
  const audible = (line: string) => {
    const { muted: mu, solo: so } = live.current;
    return so.size ? so.has(line) : !mu.has(line);
  };
  const tagOk = (tag: string) => {
    const [line, key] = tag.split('|');
    return audible(line) && covered(INSTR[key] || INSTR.Piano, live.current.counts);
  };
  const beatNow = () => {
    const e = eng.current;
    if (!e.on || !e.sampler) return score?.bars[barIdx]?.start || 0;
    return e.startBeat + Math.max(0, e.sampler.ctx.currentTime - e.t0) / e.spb;
  };

  const stop = useCallback(() => {
    const e = eng.current;
    e.on = false;
    window.clearInterval(e.timer); cancelAnimationFrame(e.raf);
    e.sampler?.hush();
    setPlaying(false);
  }, []);
  useEffect(() => () => stop(), [stop]);

  const playFrom = async (beat: number, tempo = bpm) => {
    if (!score) return;
    const e = eng.current;
    stop();
    if (!e.sampler) e.sampler = new Sampler();
    const sampler = e.sampler;
    await sampler.ctx.resume();
    const list = events.filter(ev => ev.t + ev.d > beat + 1e-6);
    const need = new Map<string, [string, number]>();
    for (const ev of list) {
      if (!audible(ev.line) || !covered(ev.instr, live.current.counts)) continue;
      need.set(`${ev.instr.patch}:${ev.m}`, [ev.instr.patch, ev.m]);
    }
    let failed = false;
    await sampler.prepare(Array.from(need.values()), (done, all) => {
      if (all && done < all) setSound(`Fetching sounds: ${done} of ${all} instruments ready.`);
    }).catch(() => { failed = true; });
    setSound(failed ? 'Some sounds could not be fetched; those lines stay silent.' : '');
    e.list = list; e.idx = 0; e.startBeat = beat; e.spb = 60 / tempo;
    e.t0 = sampler.ctx.currentTime + 0.12; e.on = true;
    setPlaying(true);
    const tick = () => {
      if (!e.on) return;
      const now = sampler.ctx.currentTime, horizon = now + 0.35;
      while (e.idx < e.list.length) {
        const ev = e.list[e.idx];
        const start = Math.max(ev.t, e.startBeat);
        const when = e.t0 + (start - e.startBeat) * e.spb;
        if (when > horizon) break;
        e.idx++;
        if (!audible(ev.line) || !covered(ev.instr, live.current.counts)) continue;
        sampler.play(ev.instr.patch, ev.m, when, (ev.t + ev.d - start) * e.spb, ev.v, `${ev.line}|${ev.instr.key}`);
      }
      if (e.startBeat + (now - e.t0) / e.spb > score.end + 1) stop();
    };
    tick();
    e.timer = window.setInterval(tick, 40);
    const starts = score.bars.map(b => b.start);
    let shown = -1;
    const frame = () => {
      if (!e.on) return;
      const b = e.startBeat + Math.max(0, sampler.ctx.currentTime - e.t0) / e.spb;
      let i = 0; while (i + 1 < starts.length && starts[i + 1] <= b + 1e-6) i++;
      if (i !== shown) { shown = i; setBarIdx(i); }
      e.raf = requestAnimationFrame(frame);
    };
    e.raf = requestAnimationFrame(frame);
  };

  const toggle = () => {
    if (playing) { const b = beatNow(); stop(); jumpToBeat(b); }
    else playFrom(score?.bars[barIdx]?.start || 0);
  };
  const jumpToBeat = (b: number) => {
    if (!score) return;
    let i = 0; while (i + 1 < score.bars.length && score.bars[i + 1].start <= b + 1e-6) i++;
    setBarIdx(i);
  };
  const goBar = (i: number) => {
    if (!score) return;
    const k = Math.max(0, Math.min(score.bars.length - 1, i));
    setBarIdx(k);
    if (playing) playFrom(score.bars[k].start);
  };
  const setTempo = (t: number) => {
    const v = Math.max(20, Math.min(160, Math.round(t) || 60));
    setBpm(v);
    if (playing) playFrom(beatNow(), v);
  };

  // A change that alters what the player should sound: hush what no longer belongs, and
  // replay from where we are when the notes themselves changed.
  useEffect(() => { eng.current.sampler?.hush(tag => !tagOk(tag)); }); // after every render; cheap
  const firstEvents = useRef(true);
  useEffect(() => {
    if (firstEvents.current) { firstEvents.current = false; return; }
    if (eng.current.on) playFrom(beatNow());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events]);

  const flip = (set: Set<string>, id: string, put: (s: Set<string>) => void) => {
    const n = new Set(set); if (n.has(id)) n.delete(id); else n.add(id); put(n);
  };

  // ------------------------------------------------------------ layers
  const current = layers.find(l => l.id === layerId) || null;
  const layerChanged = !!current && (!sameCounts(current.counts, counts) || JSON.stringify(current.reassign) !== JSON.stringify(reassign));
  const chooseLayer = (id: string) => {
    if (!id) { change({ reassign: [], layerId: null }); return; }
    const l = layers.find(x => x.id === id);
    if (l) change({ counts: Object.assign(zero(), l.counts), reassign: l.reassign.map(r => ({ ...r })), layerId: l.id });
  };
  const saveLayer = async (asNew: boolean) => {
    const name = layerName.trim();
    if (!name) { setLayerErr('A layer needs a name.'); return; }
    const id = !asNew && current ? current.id : slug(name);
    try {
      const r = await apiClient.put(`/brahms-requiem/layers/${id}`, { name, counts, reassign });
      const saved: Layer = r.data.data;
      setLayers(ls => (ls.some(l => l.id === id) ? ls.map(l => (l.id === id ? saved : l)) : [...ls, saved]));
      setLayerId(id); setSaveOpen(false);
    } catch (e) { setLayerErr(errText(e)); }
  };
  const removeLayer = async () => {
    if (!delLayer) return;
    try {
      await apiClient.delete(`/brahms-requiem/layers/${delLayer.id}`);
      setLayers(ls => ls.filter(l => l.id !== delLayer.id));
      if (layerId === delLayer.id) setLayerId(null);
      setDelLayer(null);
    } catch (e) { setStatus(`Not removed: ${errText(e)}`); setDelLayer(null); }
  };
  const addReassign = () => {
    if (!score) return;
    const first = score.bars[0].n, last = score.bars[score.bars.length - 1].n;
    if (!fLine || !fTarget) { setFErr('Choose a line and the instrument to give it to.'); return; }
    if (fTarget === fLine) { setFErr('A line cannot be given to itself.'); return; }
    if (!(fFrom >= first && fTo <= last && fFrom <= fTo)) { setFErr(`The bars must run from ${first} to ${last}, the first no later than the last.`); return; }
    setFErr('');
    change({ reassign: [...reassign, { line: fLine, bars: [fFrom, fTo], to: fTarget, octave: fOct }] });
  };

  // ------------------------------------------------------------ the map
  const L = useMemo(() => layout(counts), [counts]);
  const seats: JSX.Element[] = [];
  const pts = S.flatMap(s => L[s.id]);
  const minY = Math.min(CY, ...pts.map(p => p[1])), minX = Math.min(1000, ...pts.map(p => p[0])), maxX = Math.max(0, ...pts.map(p => p[0]));
  for (const s of S) {
    const col = FAM[s.fam].color;
    L[s.id].forEach(([x, y], i) => {
      const ghost = i === counts[s.id];
      if (ghost) {
        seats.push(<g key={`${s.id}-add`} className="seat ghost" onClick={() => bump(s.id, 1)}>
          <title>{`Add a ${s.name.toLowerCase()}`}</title>
          <circle cx={x.toFixed(1)} cy={y.toFixed(1)} r={12} /><text x={x.toFixed(1)} y={(y + 4.5).toFixed(1)} textAnchor="middle">+</text>
        </g>);
      } else {
        const r = rests(s, i, mv);
        seats.push(<g key={`${s.id}-${i}`} className={`seat${r ? ' rest' : ''}`} onClick={() => bump(s.id, -1)}>
          <title>{`${s.name} ${i + 1}${r ? ' (rests in this movement)' : ''}. Click to take this seat out.`}</title>
          <circle cx={x.toFixed(1)} cy={y.toFixed(1)} r={12.5} fill={col} /><text x={x.toFixed(1)} y={(y + 3.4).toFixed(1)} textAnchor="middle">{s.ab}</text>
        </g>);
      }
    });
  }
  const [px, py] = at(48, 90);
  const top = Math.floor(minY - 22), left = Math.floor(Math.min(minX, CX - 60) - 22), right = Math.ceil(Math.max(maxX, CX + 60) + 22);
  const all = total(counts);
  const famSum = (f: string) => S.filter(s => s.fam === f).reduce((t, s) => t + counts[s.id], 0);
  const split = ([['str', 'strings'], ['ww', 'woodwinds'], ['br', 'brass'], ['perc', 'timpani'], ['harp', 'harp']] as [string, string][])
    .map(([f, w]) => [famSum(f), w] as [number, string]).filter(([n]) => n)
    .map(([n, w]) => ((w === 'timpani' || w === 'harp') && n === 1 ? w : `${n} ${w}`)).join(', ');
  let mvline = 'All movements.';
  if (mv) {
    let p = 0;
    for (const s of S) for (let i = 0; i < counts[s.id]; i++) if (!rests(s, i, mv)) p++;
    mvline = `${mv}. ${MV[mv]}: ${p} of your ${all} players play; ${all - p} rest.`;
  }

  // ------------------------------------------------------------ the lines, for the player
  const lineRows = (score?.lines || []).map(line => {
    const instr = instrumentForName(line.name);
    const sec = instr?.section ? BY[instr.section] : null;
    const n = sec ? counts[sec.id] : 0;
    const need = sec ? partsFor(sec, score?.movement || 0) : null;
    const out = reassign.filter(r => r.line === line.id);
    const inn = reassign.filter(r => r.to === line.id);
    let state: 'ok' | 'part' | 'none' | 'free' = 'free';
    let note = instr ? (instr.section ? `${instr.label}, ${n} on the map` : `${instr.label}, not on the map`) : 'Instrument not recognised; it sounds as a piano.';
    if (sec) {
      if (!n) { state = 'none'; note = out.length ? `${instr?.label}: no players on the map, so this line is silent except in the passages given away.` : `${instr?.label}: no players on the map, so this line is silent.`; }
      else if (need && n < need) { state = 'part'; note = `${instr?.label}: ${n} ${n === 1 ? 'player' : 'players'} for Brahms's ${need} parts.`; }
      else state = 'ok';
    }
    return { line, instr, state, note, out, inn };
  });
  const lastBar = score ? score.bars[score.bars.length - 1].n : 0;
  const curBar = score?.bars[barIdx]?.n || 0;
  const targetLabel = (to: string) => {
    const t = resolveTarget(to, score);
    const isLine = score?.lines.some(l => l.id === to);
    return isLine ? to : (t?.label || to);
  };

  if (!loaded) return (<><Navigation /><div className="brq"><p className="brq-muted">Loading the plan.</p></div></>);
  if (loadError) return (<><Navigation /><div className="brq"><p className="brq-err">The plan could not be loaded: {loadError}</p></div></>);

  return (
    <>
      <Navigation />
      <div className="brq">
        <header className="brq-head">
          <div>
            <h1>Brahms, Ein deutsches Requiem</h1>
            <p className="brq-lede">The orchestra for St. Luke's, seat by seat, and Brahms's own lines played through it. Click a seat to take it out, or a dashed seat to add one. A line whose instrument has no players on the map stays silent.</p>
          </div>
          <div className="brq-undo">
            <button className="brq-btn quiet small" onClick={doUndo} disabled={!undo.length} title="Undo (Ctrl+Z)"><Undo2 size={14} /> Undo</button>
            <span className="brq-status">{status}</span>
          </div>
        </header>

        <div className="brq-tools">
          <div className="brq-bar"><span className="lab">Start from</span>
            {PRESETS.map(p => (
              <button key={p.name} className={`chip${sameCounts(p.counts, counts) ? ' on' : ''}`} onClick={() => change({ counts: { ...p.counts } })}>{p.name}</button>
            ))}
          </div>
          <div className="brq-bar"><span className="lab">Kept setups</span>
            {kept.length ? kept.map((k, i) => (
              <span className="kept" key={k.name}>
                <button className="chip" onClick={() => change({ counts: Object.assign(zero(), k.counts) })}>{k.name} ({total(k.counts)})</button>
                <button className="x" aria-label={`Remove ${k.name}`} onClick={() => setDelKept(i)}>×</button>
              </span>
            )) : <span className="brq-muted small">none yet</span>}
            <button className="brq-btn quiet small" onClick={() => { setKeepName(''); setKeepErr(''); setKeepOpen(true); }}>Keep this setup</button>
          </div>
        </div>

        <div className="brq-grid">
          <section className="brq-card brq-map">
            <div className="brq-bar tight"><span className="lab">Movement</span>
              {[0, ...ALL].map(m => (
                <button key={m} className={`chip${m === mv ? ' on' : ''}`} title={m ? MV[m] : 'All movements'} onClick={() => setMv(m)}>{m || 'All'}</button>
              ))}
            </div>
            <div className="brq-mvline">{mvline}</div>
            <svg viewBox={`${left} ${top} ${right - left} ${CY + 34 - top}`} role="img" aria-label="Orchestra seating map">
              {seats}
              <rect x={px - 9} y={py - 9} width={18} height={18} rx={3} fill="var(--fg)" />
              <text className="mark" x={px} y={py + 26} textAnchor="middle">conductor</text>
              <text className="mark" x={CX} y={CY + 22} textAnchor="middle">the nave</text>
            </svg>
          </section>

          <section className="brq-card brq-panel">
            <div className="brq-total">{all} {all === 1 ? 'player' : 'players'}</div>
            <div className="brq-split">{split}</div>
            {Object.keys(FAM).map(f => (
              <div key={f}>
                <div className="fam"><span className="dot" style={{ background: FAM[f].color }} />{FAM[f].name}</div>
                {S.filter(s => s.fam === f).map(s => {
                  const need = partsFor(s, mv), n = counts[s.id], idle = !!mv && !s.mv.includes(mv);
                  const short = need != null && n < need;
                  return (
                    <div key={s.id} className={`sec${idle ? ' resting' : ''}`}>
                      <div className="nm">{s.name}
                        {idle ? <><br /><span className="w">rests in this movement</span></> : <>
                          {need != null && <><br /><span className={`w${short ? ' short' : ''}`}>Brahms writes {need} part{need === 1 ? '' : 's'}{mv ? ' here' : ''}{short ? `; ${need - n} not covered` : ''}</span></>}
                          {s.note && <><br /><span className="w">{s.note}</span></>}
                        </>}
                      </div>
                      <button className="pm" aria-label={`One fewer ${s.name}`} disabled={!n} onClick={() => bump(s.id, -1)}>−</button>
                      <div className="n">{n}</div>
                      <button className="pm" aria-label={`One more ${s.name}`} onClick={() => bump(s.id, 1)}>+</button>
                    </div>
                  );
                })}
              </div>
            ))}
          </section>

          <section className="brq-card brq-transport" aria-label="Score player">
            <div className="brq-tr-row">
              <button className="brq-play" onClick={toggle} disabled={!score} aria-label={playing ? 'Pause' : 'Play'}>
                {playing ? <Pause size={22} /> : <Play size={22} />}
              </button>
              <div className="brq-barcount">
                <span className="lab">Bar</span><span className="big">{curBar || '–'}</span><span className="of">of {lastBar}</span>
              </div>
              <label className="brq-tempo">
                <span>Tempo</span>
                <input type="number" min={20} max={160} value={bpm} onChange={e => setTempo(Number(e.target.value))} />
                <span className="brq-muted small">quarters a minute</span>
              </label>
              <label className="brq-mvsel">
                <span>Movement</span>
                <select value={scoreMv} onChange={e => { stop(); setScoreMv(Number(e.target.value)); }}>
                  {ALL.map(m => <option key={m} value={m} disabled={!movements.includes(m)}>{m}. {MV[m]}{movements.includes(m) ? '' : ' (no notes yet)'}</option>)}
                </select>
              </label>
            </div>
            {score && (
              <div className="brq-scrub">
                <button className="brq-btn quiet small" onClick={() => goBar(barIdx - 1)} aria-label="One bar back">‹</button>
                <input type="range" min={0} max={score.bars.length - 1} value={barIdx} onChange={e => goBar(Number(e.target.value))} aria-label="Bar to start from" />
                <button className="brq-btn quiet small" onClick={() => goBar(barIdx + 1)} aria-label="One bar on">›</button>
                <label className="brq-from">From bar <input type="number" min={score.bars[0].n} max={lastBar} value={curBar}
                  onChange={e => { const k = score.bars.findIndex(b => b.n === Number(e.target.value)); if (k >= 0) goBar(k); }} /></label>
              </div>
            )}
            {(sound || scoreError) && <div className="brq-muted small">{scoreError || sound}</div>}
            {score?.source && <div className="brq-muted small">{/knutdarre/i.test(score.source) ? "Notes from knutdarre's transcription, not yet corrected." : 'Notes from the corrected score.'} The sounds are General MIDI samples, a sketch of the colours rather than the real thing.</div>}

          </section>

          <section className="brq-card brq-lines">
            <h2>Brahms's lines{score ? `, movement ${score.movement}` : ''}</h2>
            {proofs.filter(f => f.startsWith(`mvt${scoreMv}`)).map(f => (
              <div key={f} className="brq-proof small"><a href={proofUrl(f)} target="_blank" rel="noreferrer">The engraved full score of movement {scoreMv} ({f.replace(/\.pdf$/, '')})</a></div>
            ))}
            {!score && !scoreError && <p className="brq-muted small">{movements.includes(scoreMv) ? 'Loading the notes.' : 'This movement has no note data yet.'}</p>}
            <div className="brq-linelist">
              {lineRows.map(({ line, state, note, out, inn }) => {
                const isMuted = muted.has(line.id), isSolo = solo.has(line.id);
                const silent = state === 'none' || (solo.size ? !isSolo : isMuted);
                return (
                  <div key={line.id} className={`ln ${state}${silent ? ' silent' : ''}`}>
                    <div className="ln-nm">
                      <span className="ln-id">{line.id}</span>
                      <span className={`ln-w ${state}`}>{state === 'none' ? 'Uncovered. ' : ''}{note}</span>
                      {out.map((r, i) => <span key={`o${i}`} className="ln-w moved">Bars {r.bars[0]} to {r.bars[1]} go to {targetLabel(r.to)}{r.octave ? `, ${octaveWords(r.octave)}` : ''}.</span>)}
                      {inn.map((r, i) => <span key={`i${i}`} className="ln-w moved">Also plays bars {r.bars[0]} to {r.bars[1]} of {r.line}.</span>)}
                    </div>
                    <button className={`ms${isMuted ? ' on' : ''}`} aria-pressed={isMuted} title="Mute" onClick={() => flip(muted, line.id, setMuted)}>M</button>
                    <button className={`ms solo${isSolo ? ' on' : ''}`} aria-pressed={isSolo} title="Solo" onClick={() => flip(solo, line.id, setSolo)}>S</button>
                  </div>
                );
              })}
            </div>
            {(muted.size > 0 || solo.size > 0) && <button className="brq-btn quiet small" onClick={() => { setMuted(new Set()); setSolo(new Set()); }}>Clear mute and solo</button>}
          </section>

          <section className="brq-card brq-layer">
            <div className="brq-layer-a">
            <h2>Orchestration layer</h2>
            <p className="brq-muted small">A layer keeps a map setup together with the passages given to other instruments. Brahms's notes stay as he wrote them; a layer only changes who plays them.</p>
            <div className="brq-layer-pick">
              <select value={layerId || ''} onChange={e => chooseLayer(e.target.value)} aria-label="Layer">
                <option value="">Brahms as written (no passages moved)</option>
                {layers.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
              <button className="brq-btn small" onClick={() => { setLayerName(current?.name || ''); setLayerErr(''); setSaveOpen(true); }}>Save layer</button>
              {current && <button className="brq-btn quiet small danger" onClick={() => setDelLayer(current)}>Remove</button>}
            </div>
            {current && <div className={`brq-small ${layerChanged ? 'brq-warn' : 'brq-muted'}`}>{layerChanged ? `The map or the passages differ from the saved "${current.name}".` : `This is "${current.name}" as saved.`}</div>}

            <h3>Give a passage to another instrument</h3>
            {score ? (
              <div className="brq-form">
                <label>Line<select value={fLine} onChange={e => setFLine(e.target.value)}>
                  {score.lines.map(l => <option key={l.id} value={l.id}>{l.id}</option>)}
                </select></label>
                <label className="half">From bar<input type="number" min={score.bars[0].n} max={lastBar} value={fFrom} onChange={e => setFFrom(Number(e.target.value))} /></label>
                <label className="half">To bar<input type="number" min={score.bars[0].n} max={lastBar} value={fTo} onChange={e => setFTo(Number(e.target.value))} /></label>
                <label>Played by<select value={fTarget} onChange={e => setFTarget(e.target.value)}>
                  <option value="">Choose an instrument</option>
                  <optgroup label="Lines in the score">
                    {score.lines.filter(l => l.id !== fLine).map(l => <option key={l.id} value={l.id}>{l.id}</option>)}
                  </optgroup>
                  <optgroup label="Other instruments">
                    {INSTRUMENTS.map(i => <option key={i.key} value={i.key}>{i.label}</option>)}
                  </optgroup>
                </select></label>
                <label>Octave<select value={fOct} onChange={e => setFOct(Number(e.target.value))}>
                  {OCTAVES.map(([n, w]) => <option key={n} value={n}>{w[0].toUpperCase() + w.slice(1)}</option>)}
                </select></label>
                <div className="brq-form-go">
                  <button className="brq-btn small" onClick={addReassign}>Add this passage</button>
                  <button className="brq-btn quiet small" onClick={() => { setFFrom(curBar); setFTo(Math.max(curBar, fTo)); }}>Start at the current bar</button>
                </div>
                {fErr && <div className="brq-err">{fErr}</div>}
              </div>
            ) : <p className="brq-muted small">Passages can be given away once a movement's notes are loaded.</p>}

            </div>
            <div className="brq-moves">
              {reassign.length ? reassign.map((r, i) => {
                const t = resolveTarget(r.to, score);
                const off = t && !covered(t, counts);
                return (
                  <div key={i} className="mv-row">
                    <span>{r.line}, bars {r.bars[0]} to {r.bars[1]}, to {targetLabel(r.to)}{r.octave ? `, ${octaveWords(r.octave)}` : ''}.{off ? ' That instrument has no players on the map, so the passage is silent.' : ''}{!t ? ' That instrument is not known.' : ''}</span>
                    <button className="x" aria-label="Remove this passage" onClick={() => change({ reassign: reassign.filter((_, k) => k !== i) })}>×</button>
                  </div>
                );
              }) : <p className="brq-muted small">No passages are moved. Every line plays as Brahms wrote it.</p>}
            </div>
          </section>
        </div>
      </div>

      <Modal show={keepOpen} onHide={() => setKeepOpen(false)} centered>
        <Modal.Header closeButton><Modal.Title>Keep this setup</Modal.Title></Modal.Header>
        <Modal.Body>
          <label className="brq-mlabel" htmlFor="brq-kname">What should this setup be called?</label>
          <input id="brq-kname" className="form-control" autoFocus value={keepName} placeholder="For example: two horns, no bassoons"
            onChange={e => setKeepName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') (document.getElementById('brq-kdo') as HTMLButtonElement)?.click(); }} />
          {keepErr && <div className="brq-err">{keepErr}</div>}
        </Modal.Body>
        <Modal.Footer>
          <button className="brq-btn quiet" onClick={() => setKeepOpen(false)}>Cancel</button>
          <button id="brq-kdo" className="brq-btn" onClick={() => {
            const name = keepName.trim();
            if (!name) { setKeepErr('A setup needs a name.'); return; }
            setKept(k => [...k.filter(x => x.name !== name), { name, counts: { ...counts }, kept: new Date().toISOString() }]);
            setKeepOpen(false);
          }}>Keep it</button>
        </Modal.Footer>
      </Modal>

      <Modal show={delKept >= 0} onHide={() => setDelKept(-1)} centered>
        <Modal.Header closeButton><Modal.Title>Remove this setup?</Modal.Title></Modal.Header>
        <Modal.Body>{delKept >= 0 && kept[delKept] ? `"${kept[delKept].name}" will be taken off the kept setups. The setup on the map stays as it is.` : ''}</Modal.Body>
        <Modal.Footer>
          <button className="brq-btn quiet" onClick={() => setDelKept(-1)}>Cancel</button>
          <button className="brq-btn danger" onClick={() => { setKept(k => k.filter((_, i) => i !== delKept)); setDelKept(-1); }}>Remove it</button>
        </Modal.Footer>
      </Modal>

      <Modal show={saveOpen} onHide={() => setSaveOpen(false)} centered>
        <Modal.Header closeButton><Modal.Title>Save this layer</Modal.Title></Modal.Header>
        <Modal.Body>
          <p className="brq-muted small">The layer keeps the map as it stands ({all} players) and {reassign.length === 1 ? 'the one moved passage' : `the ${reassign.length} moved passages`}.</p>
          <label className="brq-mlabel" htmlFor="brq-lname">What should this layer be called?</label>
          <input id="brq-lname" className="form-control" autoFocus value={layerName} placeholder="For example: bassoon lines to the cellos"
            onChange={e => setLayerName(e.target.value)} />
          {layerErr && <div className="brq-err">{layerErr}</div>}
        </Modal.Body>
        <Modal.Footer>
          <button className="brq-btn quiet" onClick={() => setSaveOpen(false)}>Cancel</button>
          {current && <button className="brq-btn quiet" onClick={() => saveLayer(true)}>Save as a new layer</button>}
          <button className="brq-btn" onClick={() => saveLayer(false)}>{current ? `Update "${current.name}"` : 'Save layer'}</button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!delLayer} onHide={() => setDelLayer(null)} centered>
        <Modal.Header closeButton><Modal.Title>Remove this layer?</Modal.Title></Modal.Header>
        <Modal.Body>{delLayer ? `"${delLayer.name}" will be removed from the saved layers. The map and the passages on the page stay as they are.` : ''}</Modal.Body>
        <Modal.Footer>
          <button className="brq-btn quiet" onClick={() => setDelLayer(null)}>Cancel</button>
          <button className="brq-btn danger" onClick={removeLayer}>Remove it</button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default BrahmsRequiemPage;
